/**
 * tRPC Hooks for Consumer Portal
 *
 * Type-safe React hooks for consumer-facing API calls.
 * These hooks use the consumer-specific tRPC procedures.
 *
 * @module lib/trpc/hooks
 */

import {
  EsignSubmissionStatus,
  type IntakeFlowType,
  OfferStatus,
  ProposalStatus,
  type TermSheetStatusType,
} from '@ops-matrix/shared-types';
import { z } from 'zod';
import { trpc } from './client';

const UUID_SCHEMA = z.string().uuid();

// ============================================================================
// CONSUMER CASE HOOKS
// ============================================================================

export function useConsumerCases(
  input?: { page?: number; limit?: number },
  options?: { refetchInterval?: number; refetchOnWindowFocus?: boolean; enabled?: boolean },
) {
  return trpc.consumerCases.list.useQuery(input ?? { page: 1, limit: 10 }, options);
}

export function useConsumerCase(id: string | null) {
  return trpc.consumerCases.byId.useQuery(
    { id: id! },
    {
      enabled: id !== null && id !== '',
      retry: (failureCount, error) => {
        // Don't retry NOT_FOUND — the ID may be a submission ID, not a case ID.
        // CaseDetailPage handles this gracefully via getMySubmission fallback.
        if (error.data?.code === 'NOT_FOUND') return false;
        return failureCount < 3;
      },
    },
  );
}

export function useCreateConsumerCase() {
  const utils = trpc.useUtils();
  return trpc.consumerCases.create.useMutation({
    onSuccess: () => {
      void utils.consumerCases.list.invalidate();
    },
  });
}

/**
 * Withdraw a consumer case/application (Issue #809 - Application Lifecycle)
 * Sets case status to 'withdrawn' with an optional reason
 */
export function useWithdrawApplication() {
  const utils = trpc.useUtils();
  return trpc.consumerCases.withdraw.useMutation({
    onSuccess: () => {
      void utils.consumerCases.list.invalidate();
    },
  });
}

// ============================================================================
// CONSUMER DOCUMENT HOOKS
// ============================================================================

export function useConsumerDocuments(input?: { page?: number; limit?: number }) {
  return trpc.consumerDocuments.list.useQuery(input ?? { page: 1, limit: 20 });
}

export function useConsumerDocument(id: string | null) {
  return trpc.consumerDocuments.byId.useQuery({ id: id! }, { enabled: id !== null && id !== '' });
}

export function useDeleteConsumerDocument() {
  const utils = trpc.useUtils();
  return trpc.consumerDocuments.delete.useMutation({
    onSuccess: () => {
      void utils.consumerDocuments.list.invalidate();
    },
  });
}

/**
 * Upload a document for a consumer case
 * Accepts base64-encoded file data and metadata
 */
export function useUploadConsumerDocument() {
  const utils = trpc.useUtils();
  return trpc.consumerDocuments.upload.useMutation({
    onSuccess: () => {
      void utils.consumerDocuments.list.invalidate();
    },
  });
}

// ============================================================================
// ASTOR HOOKS — borrower-scoped AI surfaces (briefing, insights, predicted docs)
// ============================================================================

/**
 * Borrower dashboard briefing card. Deterministic projection in 8-agent-service
 * (see AstorBriefingService); future waves can swap for an LLM dispatch
 * without changing this hook's shape.
 */
export function useAstorBriefing(input?: { lastVisitAt?: string }) {
  return trpc.astor.getBriefing.useQuery(input ?? {});
}

/**
 * Portfolio-wide insights strip on the borrower dashboard. Returns 0-4
 * insight cards composed from existing case + lifecycle reads in
 * 8-agent-service (AstorPortfolioInsightsService — deterministic projection).
 */
export function useAstorPortfolioInsights() {
  return trpc.astor.getPortfolioInsights.useQuery();
}

/**
 * Case-scoped predicted-docs for the borrower Documents page. Returns a
 * ranked list of documents the borrower is likely to be asked for next on
 * the given case. Guards `enabled:` with the same Zod validator the
 * backend uses (`z.string().uuid()`), per `.claude/rules/portals.md` §11a2
 * "Reuse Existing Validation — Never Reinvent Parsers".
 */
export function useAstorPredictedDocs(caseId: string | null) {
  const enabled = !!caseId && UUID_SCHEMA.safeParse(caseId).success;
  return trpc.astor.getPredictedDocs.useQuery({ caseId: caseId ?? '' }, { enabled });
}

// ============================================================================
// CONSUMER PROFILE HOOKS
// ============================================================================

export function useConsumerProfile() {
  return trpc.consumerProfile.me.useQuery();
}

export function useUpdateConsumerProfile() {
  const utils = trpc.useUtils();
  return trpc.consumerProfile.update.useMutation({
    onSuccess: () => {
      void utils.consumerProfile.me.invalidate();
    },
  });
}

/**
 * Fetch consumer notification settings
 * Returns: { email: boolean, push: boolean, sms: boolean }
 */
export function useConsumerNotificationSettings() {
  return trpc.consumerProfile.notifications.get.useQuery();
}

/**
 * Update consumer notification settings
 */
export function useUpdateConsumerNotificationSettings() {
  const utils = trpc.useUtils();
  return trpc.consumerProfile.notifications.update.useMutation({
    onSuccess: () => {
      void utils.consumerProfile.notifications.get.invalidate();
    },
  });
}

// ============================================================================
// CONSUMER MESSAGE HOOKS
// ============================================================================

export function useConsumerMessages(caseId: string | null) {
  return trpc.consumerMessages.list.useQuery({ caseId: caseId! }, { enabled: caseId !== null && caseId !== '' });
}

export function useSendConsumerMessage() {
  const utils = trpc.useUtils();
  return trpc.consumerMessages.send.useMutation({
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await utils.consumerMessages.list.cancel({ caseId: variables.caseId });

      // Snapshot previous value for rollback
      const previousData = utils.consumerMessages.list.getData({ caseId: variables.caseId });

      // Optimistic update: immediately add the new message to the list
      utils.consumerMessages.list.setData({ caseId: variables.caseId }, (old) => {
        if (!old) return old;
        return {
          ...old,
          data: [
            ...old.data,
            {
              id: `optimistic-${Date.now()}`,
              caseId: variables.caseId,
              content: variables.content,
              sender: 'consumer' as const,
              senderName: 'You',
              createdAt: new Date().toISOString(),
            },
          ],
        };
      });

      return { previousData };
    },
    onError: (_err, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousData) {
        utils.consumerMessages.list.setData({ caseId: variables.caseId }, context.previousData);
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after mutation to sync with server
      void utils.consumerMessages.list.invalidate({ caseId: variables.caseId });
      void utils.consumerMessages.threads.invalidate();
    },
  });
}

// ============================================================================
// CONSUMER PIPELINE HOOKS (Issue #42)
// ============================================================================

/**
 * Fetch consumer's loan application pipeline status
 * Returns: { applicationId, stage, progress, daysInStage, nextStep, status } | null
 *
 * @param applicationId - Optional application/case ID to fetch a specific application's pipeline status.
 *                        When omitted, returns the consumer's first (active) application (backwards-compatible).
 */
export function useConsumerPipelineStatus(applicationId?: string | null) {
  return trpc.consumerPipeline.myApplication.useQuery(applicationId ? { applicationId } : undefined);
}

/**
 * Fetch document requirements and upload status
 * Returns: { checklist: DocumentChecklistItem[], totalRequired: number }
 */
export function useConsumerDocumentRequirements() {
  return trpc.consumerPipeline.documentRequirements.useQuery();
}

// ============================================================================
// CONSUMER FINDINGS HOOKS (Issue #37 - Confidence Display UI)
// ============================================================================

/**
 * Fetch document findings (extracted fields with confidence scores) for a specific document
 */
export function useConsumerDocumentFindings(documentId: string | null, options?: { page?: number; limit?: number }) {
  return trpc.consumerFindings.byDocumentId.useQuery(
    {
      documentId: documentId!,
      page: options?.page ?? 1,
      limit: options?.limit ?? 20,
    },
    { enabled: documentId !== null && documentId !== '' },
  );
}

/**
 * Fetch a single finding by ID
 */
export function useConsumerFinding(id: string | null) {
  return trpc.consumerFindings.byId.useQuery({ id: id! }, { enabled: id !== null && id !== '' });
}

/**
 * Fetch aggregated extracted fields for case review (from multiple documents)
 */
export function useConsumerExtractedFieldsForCase(documentIds: string[]) {
  return trpc.consumerFindings.extractedFieldsForCase.useQuery({ documentIds }, { enabled: documentIds.length > 0 });
}

// ============================================================================
// CONSUMER PROPOSALS HOOKS (Issues #47, #51 - Term Sheet Viewing/Acceptance)
// ============================================================================

/**
 * Fetch proposals sent to this consumer
 */
export function useConsumerProposals(input?: { page?: number; limit?: number; status?: ProposalStatus }) {
  return trpc.consumerProposals.list.useQuery({
    page: input?.page ?? 1,
    limit: input?.limit ?? 20,
    status: input?.status,
  });
}

/**
 * Fetch a single proposal by ID
 */
export function useConsumerProposal(id: string | null) {
  // Query is disabled when id is null/empty, so we provide a placeholder that won't be used
  return trpc.consumerProposals.get.useQuery(
    { id: id ?? '00000000-0000-0000-0000-000000000000' },
    { enabled: id !== null && id !== '' },
  );
}

/**
 * Accept or reject a proposal
 */
export function useRespondToProposal() {
  const utils = trpc.useUtils();
  return trpc.consumerProposals.respond.useMutation({
    onSuccess: (_data, variables) => {
      void utils.consumerProposals.list.invalidate();
      void utils.consumerProposals.get.invalidate({ id: variables.id });
    },
  });
}

/**
 * Fetch proposals linked to a specific application/case (Issue #475)
 * Enables: "View proposals for this application" in Consumer Portal
 */
export function useProposalsByCase(caseId: string | null) {
  return trpc.consumerProposals.byCase.useQuery(
    { caseId: caseId ?? '00000000-0000-0000-0000-000000000000' },
    { enabled: caseId !== null && caseId !== '' },
  );
}

// ============================================================================
// CONSUMER TERM SHEETS HOOKS (Issue #1083 - Deal-linked term sheets)
// ============================================================================

/**
 * Fetch term sheets sent to this consumer
 */
export function useConsumerTermSheets(input?: { page?: number; limit?: number; status?: TermSheetStatusType }) {
  return trpc.consumerTermSheets.list.useQuery({
    page: input?.page ?? 1,
    limit: input?.limit ?? 20,
    status: input?.status as 'sent' | 'viewed' | 'accepted' | 'rejected' | 'signing_requested' | 'signed' | undefined,
  });
}

/**
 * Fetch a single term sheet by ID
 */
export function useConsumerTermSheet(id: string | null) {
  return trpc.consumerTermSheets.get.useQuery({ termSheetId: id ?? '' }, { enabled: !!id });
}

/**
 * Accept or reject a term sheet
 */
export function useRespondToTermSheet() {
  const utils = trpc.useUtils();
  return trpc.consumerTermSheets.respond.useMutation({
    onSuccess: () => {
      void utils.consumerTermSheets.list.invalidate();
    },
  });
}

// ============================================================================
// ACTIVITY HOOKS (Issue #234 - Consumer Account Activity)
// ============================================================================

/**
 * Fetch activity on consumer's own account
 * Privacy-focused: Only shows relevant events visible to the consumer
 * @see Issue #234 - Consumer Portal "Activity on my account" section
 */
export function useConsumerAccountActivity(input?: { page?: number; limit?: number }) {
  return trpc.consumerActivity.myAccount.useQuery({
    page: input?.page ?? 1,
    limit: input?.limit ?? 20,
  });
}

/**
 * Fetch activity for a specific case owned by the consumer
 * @see Issue #234 - Show who viewed/modified consumer's cases
 */
export function useConsumerCaseActivity(caseId: string | null, input?: { page?: number; limit?: number }) {
  return trpc.consumerActivity.byCase.useQuery(
    {
      caseId: caseId!,
      page: input?.page ?? 1,
      limit: input?.limit ?? 20,
    },
    { enabled: caseId !== null && caseId !== '' },
  );
}

// ============================================================================
// E-SIGN HOOKS (Issue #300 - DocuSeal Integration)
// ============================================================================

/**
 * Fetch e-sign submissions for a case
 * Used to show signing requests on case detail page
 */
export function useEsignSubmissions(input?: { caseId?: string; consumerId?: string; status?: string }) {
  return trpc.esign.listSubmissions.useQuery(
    {
      caseId: input?.caseId,
      consumerId: input?.consumerId,
      status: input?.status as EsignSubmissionStatus | undefined,
    },
    { enabled: !!(input?.caseId || input?.consumerId) },
  );
}

/**
 * Fetch a single e-sign submission by ID
 */
export function useEsignSubmission(submissionId: number | null) {
  return trpc.esign.getSubmission.useQuery(
    { submissionId: submissionId! },
    { enabled: submissionId !== null && submissionId > 0 },
  );
}

/**
 * Get embed URL for signing
 * Returns the DocuSeal iframe URL for a submitter
 */
export function useEsignEmbedUrl(submitterId: number | null) {
  return trpc.esign.getEmbedUrl.useQuery(
    { submitterId: submitterId! },
    { enabled: submitterId !== null && submitterId > 0 },
  );
}

/**
 * Issue #1274 — Fetch the list of required disclosure templates for the consumer
 * intake form. Backed by active templates with `category: "disclosure"`.
 */
export function useRequiredDisclosures(options?: { enabled?: boolean }) {
  return trpc.esign.getRequiredDisclosures.useQuery(undefined, {
    staleTime: 5 * 60 * 1000, // Rarely change; cache 5 minutes.
    // The procedure is `protectedProcedure` and depends on `ctx.user.businessId`.
    // The consumer apply page is mounted anonymously — gating with `enabled`
    // prevents the unauthenticated query from firing the global UNAUTHORIZED
    // redirect in `client.ts`'s `errorLink`, which would bounce the
    // anonymous applicant away from the form.
    enabled: options?.enabled ?? true,
  });
}

/**
 * Issue #1274 — Create and send a signing envelope per disclosure template in one call.
 * Used on the consumer intake form step 4 to prepare real DocuSeal submitter IDs
 * for embedded signing.
 */
export function useCreateDisclosureEnvelopes() {
  return trpc.esign.createDisclosureEnvelopes.useMutation();
}

/**
 * Issue #1407 — Poll envelope statuses for the consumer's own disclosure
 * envelopes. Refetches on window focus so the counter updates when the user
 * signs in another tab/device and switches back. Polls every 5s while any
 * envelope is still pending so the counter eventually catches up even if
 * the focus event doesn't fire (headless browser, alt-tab without focus, etc.).
 *
 * Pass `hasPending=false` once all envelopes are signed to stop polling.
 */
export function useDisclosureEnvelopeStatuses(envelopeIds: string[], hasPending: boolean) {
  return trpc.esign.getDisclosureEnvelopeStatuses.useQuery(
    { envelopeIds },
    {
      enabled: envelopeIds.length > 0,
      refetchOnWindowFocus: true,
      refetchInterval: hasPending ? 5_000 : false,
      staleTime: 2_000,
    },
  );
}

/**
 * Download signed document
 * @param submissionId - The ID of the e-sign submission to download
 * @param options - Optional query options (e.g., enabled flag)
 */
export function useDownloadSignedDocument(submissionId: number, options?: { enabled?: boolean }) {
  return trpc.esign.downloadDocument.useQuery({ submissionId }, { enabled: options?.enabled ?? !!submissionId });
}

// ============================================================================
// DOCUMENTS TO SIGN HOOKS (Issue #1308 - Consumer portal list)
// ============================================================================

/**
 * List pending + recently-signed signatures for the authenticated consumer.
 *
 * Backs:
 * - `/documents-to-sign` page (pending tab + signed tab)
 * - Home widget (top 3 pending)
 * - Nav badge (pending count)
 *
 * Refetches automatically on window focus and every 60s while mounted.
 * The backend gates this behind ENABLE_TERMSHEET_SIGNING_V2 and returns
 * NOT_FOUND when the flag is off — callers should handle that as "hide".
 *
 * Gracefully returns { pending: [], signed: [] } if the service is
 * unavailable (UNAVAILABLE / ECONNREFUSED).
 */
export function useListPendingSignatures(options?: { refetchInterval?: number; enabled?: boolean }) {
  // Namespaced as `signingDocuments` (not `documents`) in the tRPC root to
  // avoid collision with the pre-existing `documents` entry from the
  // business router. See 2-api-gateway/src/trpc/trpc.router.ts.
  return trpc.signingDocuments.listPendingSignatures.useQuery(undefined, {
    // 60s poll per issue requirement. refetchOnWindowFocus is true by default
    // in this project's trpc client, which is the "updates on focus" behavior
    // the issue calls for.
    refetchInterval: options?.refetchInterval ?? 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // Feature flag off → NOT_FOUND; don't hammer the server.
      if (error.data?.code === 'NOT_FOUND') return false;
      return failureCount < 2;
    },
    enabled: options?.enabled ?? true,
  });
}

// ============================================================================
// HEALTH CHECK
// ============================================================================

export function useHealthCheck() {
  return trpc.health.useQuery();
}

// ============================================================================
// LOAN APPLICATION HOOKS (Issue #387 - Partner Referral Flow)
// ============================================================================

/**
 * Submit a loan application with partner attribution
 * Creates a case in the backend and handles referral tracking
 *
 * @example
 * ```tsx
 * const submitApplication = useSubmitApplication();
 * await submitApplication.mutateAsync({
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john@example.com',
 *   // ... other fields
 *   referralCode: 'PARTNER123',
 * });
 * ```
 */
export function useSubmitApplication() {
  const utils = trpc.useUtils();
  return trpc.submitApplication.useMutation({
    onSuccess: () => {
      // Invalidate submissions list (application creates IntakeSubmission for review)
      void utils.mySubmissions.invalidate();
      // Also invalidate cases in case of auto-create case flow (invitation-based)
      void utils.consumerCases.list.invalidate();
      void utils.consumerPipeline.myApplication.invalidate();
    },
  });
}

/**
 * Resubmit a rejected application with updated form data.
 * Updates the existing submission instead of creating a new one.
 */
export function useResubmitApplication() {
  const utils = trpc.useUtils();
  return trpc.resubmitApplication.useMutation({
    onSuccess: () => {
      void utils.mySubmissions.invalidate();
      void utils.getMySubmission.invalidate();
      void utils.consumerCases.list.invalidate();
      void utils.consumerPipeline.myApplication.invalidate();
    },
  });
}

/**
 * List consumer's own intake submissions
 * Shows submissions pending review, converted to cases, or rejected
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useMySubmissions({ page: 1, limit: 10 });
 * data?.submissions.map(s => <SubmissionCard key={s.id} submission={s} />);
 * ```
 */
export function useMySubmissions(
  options: { page?: number; limit?: number; status?: string } = {},
  queryOptions?: { enabled?: boolean },
) {
  return trpc.mySubmissions.useQuery(
    {
      page: options.page || 1,
      limit: options.limit || 20,
      status: options.status as any,
    },
    queryOptions,
  );
}

/**
 * Fetch a single intake submission by ID (consumer-facing)
 * Used when viewing an unconverted submission from the Applications list
 */
export function useMySubmission(id: string | null) {
  return trpc.getMySubmission.useQuery({ id: id! }, { enabled: id !== null && id !== '' });
}

// ============================================================================
// REFERRAL TRACKING HOOKS (Issue #387 - Partner Referral Flow)
// ============================================================================

/**
 * Record a click when user lands on the application page with a referral code
 * This is called BEFORE user authentication
 *
 * @example
 * ```tsx
 * const recordClick = useRecordReferralClick();
 * useEffect(() => {
 *   if (referralCode) {
 *     recordClick.mutate({ trackingCode: referralCode });
 *   }
 * }, [referralCode]);
 * ```
 */
export function useRecordReferralClick() {
  return trpc.referralTracking.recordClick.useMutation();
}

/**
 * Validate a referral code without recording a click
 * Used to check if a code is valid before showing partner branding
 *
 * @example
 * ```tsx
 * const { data } = useValidateReferralCode(referralCode);
 * if (data?.valid) {
 *   showPartnerBranding(data.partnerName);
 * }
 * ```
 */
export function useValidateReferralCode(trackingCode: string | null) {
  return trpc.referralTracking.validateCode.useQuery(
    { trackingCode: trackingCode! },
    { enabled: trackingCode !== null && trackingCode !== '' },
  );
}

// ============================================================================
// INTAKE TEMPLATES HOOKS (Issue #432 - Templated Loan Intake Wizard)
// ============================================================================

/**
 * Fetch a custom intake template by ID
 * Requires both businessId and templateId for multi-tenancy security
 */
export function useIntakeTemplateById(businessId: string | null, templateId: string | null) {
  return trpc.intakeTemplates.getById.useQuery(
    { businessId: businessId!, templateId: templateId! },
    { enabled: businessId !== null && businessId !== '' && templateId !== null && templateId !== '' },
  );
}

/**
 * Fetch custom intake templates for a business
 * Used when consumer lands with businessId but no templateId
 */
export function useIntakeTemplatesForBusiness(businessId: string | null, intakeType?: IntakeFlowType) {
  return trpc.intakeTemplates.getForBusiness.useQuery(
    { businessId: businessId!, intakeType },
    { enabled: businessId !== null && businessId !== '' },
  );
}

// ============================================================================
// INTAKE PRE-FILL HOOKS (Issue #475 - Lead Conversion & Partner Referral)
// ============================================================================

/**
 * Fetch pre-fill data using a token
 * Used when consumer lands with a prefill token from lead conversion or partner referral
 * Returns form data to pre-populate the intake wizard
 */
export function usePrefillData(token: string | null) {
  return trpc.intake.getPrefillData.useQuery({ token: token! }, { enabled: token !== null && token !== '' });
}

// ============================================================================
// GLOBAL SEARCH HOOKS (Issue #236)
// ============================================================================

/**
 * Global search for consumer portal
 * Searches cases and documents visible to the consumer
 * Returns a query-based search that triggers when input.query.length >= 2
 *
 * @example
 * const { data, isLoading } = useGlobalSearch({
 *   query: searchQuery,
 *   entityTypes: ['case', 'document'],
 *   limit: 10,
 * });
 */
export function useGlobalSearch(input: {
  query: string;
  entityTypes?: ('case' | 'document')[];
  limit?: number;
  highlightFields?: string[];
  includeFacets?: boolean;
}) {
  return trpc.search.global.useQuery(
    { ...input, portal: 'consumer' as const },
    {
      enabled: input.query.length >= 2,
      staleTime: 1000 * 30,
    },
  );
}

/**
 * Global search query (for reactive search)
 * Portal: consumer - generates URLs like /applications/:id, /documents/:id
 */
export function useGlobalSearchQuery(
  input: {
    query: string;
    entityTypes?: ('case' | 'document')[];
    limit?: number;
    highlightFields?: string[];
    includeFacets?: boolean;
  } | null,
) {
  return trpc.search.global.useQuery(
    input ? { ...input, portal: 'consumer' as const } : { query: '', limit: 10, portal: 'consumer' as const },
    {
      enabled: input !== null && input.query.length >= 2,
      staleTime: 1000 * 30, // Cache for 30 seconds
    },
  );
}

// ============================================================================
// BUSINESS BRANDING HOOKS (Issue #522 - White-Label Consumer Portal)
// ============================================================================

/**
 * Fetch business branding for white-label experience
 * Used by unauthenticated consumers to see the inviting business's branding
 *
 * @example
 * ```tsx
 * const { data: branding } = useBusinessBranding(businessIdFromUrl);
 * <Typography>{branding?.name || 'Fiops'}</Typography>
 * ```
 */
export function useBusinessBranding(businessId: string | null) {
  return trpc.businessProfile.getBrandingPublic.useQuery(
    { businessId: businessId! },
    { enabled: businessId !== null && businessId !== '', staleTime: 1000 * 60 * 5 }, // Cache for 5 minutes
  );
}

/**
 * Fetch investment type configuration for intake forms
 * Used by unauthenticated consumers to see which investment types are available
 * @see Issue #526 - Configurable Intake Form Investment Types
 *
 * @example
 * ```tsx
 * const { data: config } = useInvestmentTypeConfig(businessIdFromUrl);
 * const availableTypes = config?.supportedInvestmentTypes || ['bridge', 'construction', 'rental', 'commercial'];
 * const showGeneral = config?.alwaysShowGeneral ?? true;
 * ```
 */
export function useInvestmentTypeConfig(businessId: string | null) {
  return trpc.userTemplates.getInvestmentTypeConfigPublic.useQuery(
    { businessId: businessId! },
    { enabled: businessId !== null && businessId !== '', staleTime: 1000 * 60 * 5 }, // Cache for 5 minutes
  );
}

// ============================================================================
// BORROWING ENTITY & GUARANTOR HOOKS (Issue #636)
// ============================================================================

export function useMyBorrowingEntities(input?: { page?: number; limit?: number; consumerId?: string }) {
  return trpc.loanEntities.list.useQuery(input ?? { page: 1, limit: 20 });
}

export function useMyBorrowingEntity(id: string | null) {
  return trpc.loanEntities.byId.useQuery({ id: id! }, { enabled: id !== null && id !== '' });
}

export function useMyGuarantees(input?: { consumerId?: string; borrowingEntityId?: string }) {
  return trpc.loanGuarantors.list.useQuery(input ?? {});
}

export function useMyGuarantorExposure(input?: { consumerId?: string; borrowingEntityId?: string }) {
  return trpc.loanGuarantors.getExposure.useQuery(input ?? {}, {
    enabled: !!(input?.consumerId || input?.borrowingEntityId),
  });
}

export function useMyEntityOwnershipTree(borrowingEntityId: string | null) {
  return trpc.loanEntities.getOwnershipTree.useQuery(
    { borrowingEntityId: borrowingEntityId ?? '' },
    { enabled: borrowingEntityId !== null },
  );
}

/**
 * List borrowers linked to a case (read-only, for Parties tab)
 * Gap #7, Item #11 - Consumer Parties visibility
 */
export function useCaseBorrowers(caseId: string | null) {
  return trpc.caseBorrowers.list.useQuery({ caseId: caseId! }, { enabled: caseId !== null && caseId !== '' });
}

/**
 * List case-borrower links filtered by borrowing entity ID
 * Issue #1199 — Consumer entity-to-cases navigation
 * Uses existing caseBorrowers.list with borrowingEntityId filter
 */
export function useCasesByEntity(borrowingEntityId: string | null) {
  return trpc.caseBorrowers.list.useQuery(
    { borrowingEntityId: borrowingEntityId! },
    { enabled: borrowingEntityId !== null && borrowingEntityId !== '' },
  );
}

/**
 * List guarantors linked to a case (read-only, for Parties tab)
 * Gap #7, Item #11 - Consumer Parties visibility
 */
export function useCaseGuarantors(caseId: string | null) {
  return trpc.loanGuarantors.list.useQuery({ caseId: caseId! }, { enabled: caseId !== null && caseId !== '' });
}

// ============================================================================
// CONSUMER NOTIFICATION HOOKS (Issue #699 - Notification System Fidelity)
// ============================================================================

/**
 * Fetch consumer's notifications with pagination and filtering
 */
export function useConsumerNotificationsList(input?: {
  page?: number;
  limit?: number;
  status?: 'all' | 'unread' | 'read';
}) {
  return trpc.consumerNotifications.list.useQuery(
    {
      page: input?.page ?? 1,
      limit: input?.limit ?? 20,
      status: input?.status ?? 'all',
    },
    {
      staleTime: 30_000,
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
    },
  );
}

/**
 * Fetch unread notification count for the bell badge
 * Polls every 30 seconds
 */
export function useConsumerNotificationsUnreadCount() {
  return trpc.consumerNotifications.unreadCount.useQuery(undefined, {
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
}

/**
 * Mark a single notification as read
 */
export function useMarkNotificationAsRead() {
  const utils = trpc.useUtils();
  return trpc.consumerNotifications.markAsRead.useMutation({
    onSuccess: () => {
      void utils.consumerNotifications.list.invalidate();
      void utils.consumerNotifications.unreadCount.invalidate();
    },
  });
}

/**
 * Mark a single notification as unread
 */
export function useMarkNotificationAsUnread() {
  const utils = trpc.useUtils();
  return trpc.consumerNotifications.markAsUnread.useMutation({
    onSuccess: () => {
      void utils.consumerNotifications.list.invalidate();
      void utils.consumerNotifications.unreadCount.invalidate();
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const utils = trpc.useUtils();
  return trpc.consumerNotifications.markAllAsRead.useMutation({
    onSuccess: () => {
      void utils.consumerNotifications.list.invalidate();
      void utils.consumerNotifications.unreadCount.invalidate();
    },
  });
}

/**
 * Fetch consumer notification preferences
 */
export function useConsumerNotificationPreferences() {
  return trpc.consumerNotifications.getPreferences.useQuery();
}

/**
 * Update consumer notification preferences
 */
export function useUpdateConsumerNotificationPreferences() {
  const utils = trpc.useUtils();
  return trpc.consumerNotifications.updatePreferences.useMutation({
    onSuccess: () => {
      void utils.consumerNotifications.getPreferences.invalidate();
    },
  });
}

// ============================================================================
// CONSUMER QUOTE HOOKS (Issue #809 - Quote Lifecycle)
// ============================================================================

/**
 * Fetch consumer's quotes with pagination and optional status filter
 */
export function useConsumerQuotes(input?: { page?: number; limit?: number; status?: OfferStatus }) {
  return trpc.consumerQuotes.list.useQuery({
    page: input?.page ?? 1,
    limit: input?.limit ?? 20,
    status: input?.status,
  });
}

/**
 * Fetch a single quote by ID with calculation details
 */
export function useConsumerQuoteDetail(quoteId: string | null) {
  return trpc.consumerQuotes.get.useQuery(
    { quoteId: quoteId ?? '00000000-0000-0000-0000-000000000000' },
    { enabled: quoteId !== null && quoteId !== '' },
  );
}

/**
 * Accept a quote
 */
export function useAcceptQuote() {
  const utils = trpc.useUtils();
  return trpc.consumerQuotes.accept.useMutation({
    onSuccess: () => {
      void utils.consumerQuotes.list.invalidate();
    },
  });
}

/**
 * Reject a quote with an optional reason
 */
export function useRejectQuote() {
  const utils = trpc.useUtils();
  return trpc.consumerQuotes.reject.useMutation({
    onSuccess: () => {
      void utils.consumerQuotes.list.invalidate();
    },
  });
}

/**
 * Submit a counter-offer on a quote (Issue #809 - Quote Counter-Offer Flow)
 * Allows proposing alternative rate, term, or amount with a reason
 */
export function useCounterOfferQuote() {
  const utils = trpc.useUtils();
  return trpc.consumerQuotes.counterOffer.useMutation({
    onSuccess: () => {
      void utils.consumerQuotes.list.invalidate();
    },
  });
}

// ============================================================================
// AI CHAT HISTORY HOOKS (Issue #809 - Feature 7: Persistent Message History)
// ============================================================================

/**
 * Fetch chat history for the consumer AI assistant
 * Returns previous messages to restore on page load
 *
 * @param agentId - Agent ID/slug (defaults to 'consumer-assistant')
 * @param options - Query options (enabled flag)
 */
export function useChatHistory(agentId?: string, options?: { enabled?: boolean }) {
  return trpc.consumerChat.getHistory.useQuery(
    { agentId: agentId ?? 'consumer-assistant', limit: 100 },
    {
      enabled: options?.enabled ?? true,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * Clear chat history for the consumer AI assistant
 * Deletes all messages and the conversation record
 */
export function useClearChatHistory() {
  const utils = trpc.useUtils();
  return trpc.consumerChat.clearHistory.useMutation({
    onSuccess: () => {
      void utils.consumerChat.getHistory.invalidate();
    },
  });
}

/**
 * Save a single message to chat history
 * Used to persist user messages and completed assistant responses
 */
export function useSaveChatMessage() {
  return trpc.consumerChat.saveMessage.useMutation();
}

// ============================================================================
// HOME WATCHLIST HOOKS (ported from business portal — Issue #1424 cross-portal port)
// ============================================================================
// Backed by 24-account-service via the `home.watchlist.*` tRPC routes that
// already exist on the global appRouter. The home router scopes everything
// to the calling user's businessId/userId, so consumers automatically only
// see their own pins.
//
// Consumers only pin entities they own — `case`/`loan`/`application` — but
// the underlying enum supports more values for parity with the other portals.

/** List the current consumer's pinned entities. */
export function useHomeWatchlist(input?: {
  entityType?: 'case' | 'loan' | 'application' | 'partner' | 'contact' | 'consumer';
}) {
  return trpc.home.watchlist.list.useQuery(input ?? {});
}

/** Add a pin. */
export function useAddHomePin() {
  const utils = trpc.useUtils();
  return trpc.home.watchlist.add.useMutation({
    onSuccess: () => {
      void utils.home.watchlist.list.invalidate();
    },
  });
}

/** Remove a pin. */
export function useRemoveHomePin() {
  const utils = trpc.useUtils();
  return trpc.home.watchlist.remove.useMutation({
    onSuccess: () => {
      void utils.home.watchlist.list.invalidate();
    },
  });
}
